import { ThemeOptions } from '@mui/material';

// Colors

const background = {
  default: '#171c24',
  paper: '#222b36',
  opacity: '#29425fff',
  divider: '#919eab3d',
};

const divider = '#919eab3d';

const primary = {
  main: '#8395d5',
  light: '#8EAAFB',
  dark: '#6f80ad',
  contrastText: '#fff',
};

const secondary = {
  main: '#438ABD',
  light: '#3FC79A',
  dark: '#2d6b91',
  contrastText: '#fff',
};

const success = {
  main: '#6FBCB3',
  light: '#43C6B7',
  dark: '#4f8e85',
  contrastText: '#fff',
};

const info = {
  main: '#D4D8D9',
  light: '#64B6F7',
  dark: '#b7b7b7',
  contrastText: '#001b2f',
};

const warning = {
  main: '#FF9E42',
  light: '#FFBF4C',
  dark: '#d88541',
  contrastText: '#000',
};

const error = {
  main: '#b61f3c',
  light: '#DA6868',
  dark: '#911a2f',
  contrastText: '#fff',
};

const text = {
  primary: '#EDF2F7',
  secondary: '#A0AEC0',
  disabled: 'rgba(255, 255, 255, 0.48)',
};

/** Botón flotante sólido. Más saturado que `primary.main`, que es el acento de texto/iconos. */
const floatingPrimary = '#5b6ecf';
const floatingPrimaryHover = '#4a5cb8';
const floatingPrimaryShadow = 'rgba(91,110,207,0.4)';

export const darkThemeOptions: ThemeOptions = {
  components: {
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            opacity: 1,
            color: text.secondary,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${divider}`,
        },
      },
    },
    // Acciones flotantes de los tableros operacionales, calcadas del mockup
    // digital-product-guidelines/src/pages/tableros-operacionales
    // (.to-btn-floating-outline / .to-btn-floating-primary).
    // Se estila acá y no inline porque `variant="extended"` se usa únicamente en
    // metricsViews/components/MetricsView.tsx, un archivo con reescrituras en vuelo.
    MuiFab: {
      styleOverrides: {
        extended: ({ ownerState }) => ({
          gap: 8,
          height: 'auto',
          borderRadius: 999,
          padding: '12px 20px',
          fontSize: 13,
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          // outline: default y color="info" (ej. "Lista de visualizaciones")
          ...((ownerState.color === undefined ||
            ownerState.color === 'default' ||
            ownerState.color === 'inherit' ||
            ownerState.color === 'info') && {
            backgroundColor: 'rgba(34,43,54,0.95)',
            backdropFilter: 'blur(8px)',
            border: `1px solid ${divider}`,
            color: text.primary,
            '&:hover': { backgroundColor: 'rgba(45,54,66,0.95)' },
          }),
          // sólido: color="primary" (ej. "Editar", "Guardar Layout")
          ...(ownerState.color === 'primary' && {
            backgroundColor: floatingPrimary,
            padding: '12px 22px',
            fontWeight: 700,
            color: '#fff',
            boxShadow: `0 8px 24px ${floatingPrimaryShadow}`,
            '&:hover': { backgroundColor: floatingPrimaryHover },
          }),
        }),
      },
    },
  },
  palette: {
    action: {
      active: '#fff',
      hover: 'rgba(255, 255, 255, 0.04)',
      selected: 'rgba(255, 255, 255, 0.08)',
      disabledBackground: 'rgba(255, 255, 255, 0.12)',
      disabled: 'rgba(255, 255, 255, 0.26)',
    },
    background,
    divider,
    error,
    info,
    mode: 'dark',
    primary,
    secondary,
    success,
    text,
    warning,
  },
  shadows: [
    'none',
    '0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 1px 2px rgba(0, 0, 0, 0.24)',
    '0px 1px 4px rgba(0, 0, 0, 0.24)',
    '0px 1px 5px rgba(0, 0, 0, 0.24)',
    '0px 1px 6px rgba(0, 0, 0, 0.24)',
    '0px 2px 6px rgba(0, 0, 0, 0.24)',
    '0px 3px 6px rgba(0, 0, 0, 0.24)',
    '0px 4px 6px rgba(0, 0, 0, 0.24)',
    '0px 5px 12px rgba(0, 0, 0, 0.24)',
    '0px 5px 14px rgba(0, 0, 0, 0.24)',
    '0px 5px 15px rgba(0, 0, 0, 0.24)',
    '0px 6px 15px rgba(0, 0, 0, 0.24)',
    '0px 7px 15px rgba(0, 0, 0, 0.24)',
    '0px 8px 15px rgba(0, 0, 0, 0.24)',
    '0px 9px 15px rgba(0, 0, 0, 0.24)',
    '0px 10px 15px rgba(0, 0, 0, 0.24)',
    '0px 12px 22px -8px rgba(0, 0, 0, 0.24)',
    '0px 13px 22px -8px rgba(0, 0, 0, 0.24)',
    '0px 14px 24px -8px rgba(0, 0, 0, 0.24)',
    '0px 20px 25px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
    '0px 25px 50px rgba(0, 0, 0, 0.24)',
  ],
};
